import React, {useState} from "react";
import {useAtomValue} from "jotai";
import {Collapse, IconButton, Stack, Typography} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {Farm} from "../../types/domain/farm";
import {FieldsTable} from "../fields/FieldsTable";
import {FarmCreateDialog} from "../dialogs/FarmCreateDialog";
import {isUserHaveAnyOfGlobalRoles} from "../../utils/accessControlUtils";
import {UserGlobalRole} from "../../types/domain/user";
import {authenticatedUserLoadable} from "../../store/authenticatedUserStore";
import {Field} from "../../types/domain/field";
import {EditIcon} from "../icons/EditIcon";
import {useMeasureSystemUtils} from "../../hooks/measureSystemUtilsHooks";
import {useResourceValueOrDefault} from "../../hooks/resourceHooks";

export interface Props {
    farms: Farm[];
    fields: Field[];
    refreshFarms: () => void;
    refreshFields: () => void;
    companyId: string;
}

interface FarmCompProps {
    farm: Farm;
    companyId: string;
    fields: Field[];
    refreshFarms: () => void;
    refreshFields: () => void;
}

const FarmComp: React.FC<FarmCompProps> = ({farm, companyId, refreshFarms, refreshFields, fields}) => {
    const authenticatedUserResource = useAtomValue(authenticatedUserLoadable);
    const authenticatedUser = useResourceValueOrDefault(authenticatedUserResource, undefined);
    const msUtils = useMeasureSystemUtils();
    const isAdmin =
        authenticatedUser !== undefined &&
        isUserHaveAnyOfGlobalRoles(authenticatedUser, [UserGlobalRole.ADMIN, UserGlobalRole.CS]);
    const defaultExpand = farm.isDefault;
    const [isExpanded, setIsExpanded] = useState(defaultExpand);
    const toggleExpand = () => setIsExpanded(!isExpanded);
    const [editDialogOpened, setEditDialogOpened] = useState<boolean>(false);
    const onEditDialogClose = (isSuccessful: boolean) => {
        setEditDialogOpened(false);
        if (isSuccessful) {
            refreshFarms();
        }
    };
    return (
        <>
            <Stack direction="row" alignItems="center" justifyContent="flex-start">
                <IconButton
                    sx={{padding: 0}}
                    onClick={(e) => {
                        if (fields && fields.length > 0) {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleExpand();
                        }
                    }}
                >
                    {isExpanded ? (
                        <ExpandMoreIcon fontSize="small" color="primary" />
                    ) : (
                        <ChevronRightIcon fontSize="small" color="primary" />
                    )}
                </IconButton>

                <Stack
                    direction="row"
                    alignItems="center"
                    useFlexGap
                    justifyContent="space-between"
                    spacing={6}
                    sx={{
                        width: "100%",
                        borderBottomWidth: "1px",
                        borderBottomStyle: "solid",
                        borderBottomColor: "#E9EBF1",
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        useFlexGap
                        justifyContent="space-between"
                        sx={{p: 2}}
                        spacing={2}
                    >
                        <Typography textTransform="uppercase" fontSize={14}>
                            {farm.name}
                        </Typography>

                        <Typography textTransform="uppercase" fontSize={14}>
                            {msUtils.getArea(farm.areaInHa)}, {msUtils.getAreaUnits()}
                        </Typography>
                    </Stack>
                    <IconButton
                        color="primary"
                        onClick={() => {
                            setEditDialogOpened(true);
                        }}
                        sx={{
                            paddingRight: "2rem",
                        }}
                    >
                        <EditIcon
                            sx={{
                                stroke: "#303A8C",
                                width: "1.125rem",
                                height: "1.125rem",
                            }}
                        />
                    </IconButton>
                </Stack>
            </Stack>
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                <FieldsTable
                    farmId={farm.id}
                    companyId={companyId}
                    isAdmin={isAdmin}
                    fields={fields}
                    refresh={refreshFields}
                />
            </Collapse>

            {editDialogOpened && companyId && farm && (
                <FarmCreateDialog
                    mode="edit"
                    companyId={companyId}
                    selectedFarm={farm}
                    open={editDialogOpened}
                    onClose={onEditDialogClose}
                />
            )}
        </>
    );
};

export const FarmsTable: React.FC<Props> = ({farms, refreshFarms, companyId, fields, refreshFields}) => {
    return (
        <Stack direction="column">
            {farms.map((farm) => {
                const filteredFields = fields ? fields.filter((item) => item.farmId == farm.id) : [];
                return (
                    <FarmComp
                        key={farm.id}
                        farm={farm}
                        companyId={companyId}
                        refreshFarms={refreshFarms}
                        refreshFields={refreshFields}
                        fields={filteredFields}
                    />
                );
            })}
        </Stack>
    );
};
